import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery, toQueryParams } from 'helpers/api';

/**
 * Candidate Portal API
 *
 * Handles all candidate portal authentication and data endpoints.
 *
 * SECURITY MODEL:
 * - Uses a separate HttpOnly session cookie from the main app, set by the API
 * - All protected endpoints require portal authentication
 * - Authorization is validated server-side via candidate_user_links
 */
export const candidatePortalApi = createApi({
  reducerPath: 'candidatePortalApi',
  baseQuery: createBaseQuery('candidate_portal', { timeout: 0 }),
  tagTypes: [
    'PortalSession',
    'PortalDashboard',
    'PortalCheck',
    'PortalSubCheck',
    'PortalConsentFiles',
    'PortalUploads',
  ],
  endpoints: builder => ({
    // ==========================================
    // PUBLIC ENDPOINTS
    // ==========================================

    /**
     * Decode invitation token to get context
     */
    decodeInvitation: builder.query({
      query: token => `/invitation/${token}`,
      transformResponse: response => response.result,
    }),

    /**
     * Request OTP for login
     */
    requestOtp: builder.mutation({
      query: ({ email, candidateId, companyId }) => ({
        url: '/auth/otp/request',
        method: 'POST',
        body: {
          email,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: response => ({
        result: response.result,
        error: response.error,
      }),
    }),

    /**
     * Verify OTP and login
     */
    verifyOtp: builder.mutation({
      query: ({ email, otp, candidateId, companyId }) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        body: {
          email,
          otp,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalSession'],
    }),

    /**
     * Login with password
     */
    loginWithPassword: builder.mutation({
      query: ({ email, password, candidateId, companyId }) => ({
        url: '/auth/password',
        method: 'POST',
        body: {
          email,
          password,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalSession'],
    }),

    // ==========================================
    // AUTHENTICATED ENDPOINTS
    // ==========================================

    /**
     * Get current session info
     */
    getSession: builder.query({
      query: () => '/auth/session',
      transformResponse: response => response.result,
      providesTags: ['PortalSession'],
    }),

    logoutPortal: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['PortalSession'],
    }),

    /**
     * Set password (after first OTP login)
     */
    setPassword: builder.mutation({
      query: password => ({
        url: '/auth/password/set',
        method: 'POST',
        body: { password },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalSession'],
    }),

    /**
     * Select candidate context (when user has multiple)
     * Note: company_id is derived from the candidate on the backend
     */
    selectContext: builder.mutation({
      query: ({ candidateId }) => ({
        url: '/context/select',
        method: 'POST',
        body: {
          candidate_id: candidateId,
        },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalSession', 'PortalDashboard'],
    }),

    // ==========================================
    // PROTECTED ENDPOINTS (require context)
    // ==========================================

    /**
     * Get dashboard data
     */
    getDashboard: builder.query({
      query: () => '/dashboard',
      transformResponse: response => response.result,
      providesTags: ['PortalDashboard'],
    }),

    /**
     * Get check result file.
     * Returns a signed HTTPS URL when backend provides one (e.g. afp_backy), or a data URL
     * from the streamed file so we avoid blob: URLs and CSP issues.
     */
    getCheckResultFile: builder.query({
      queryFn: async ({ candidateCheckId }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/checks/${candidateCheckId}/result`,
          responseHandler: async response => {
            const contentType = response.headers.get('content-type');
            // Backend may return JSON with a signed URL (e.g. afp_backy) or stream the file
            if (contentType?.includes('application/json')) {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
            // Streamed file: use data URL so we don't need blob: in CSP
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            });
          },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: response.data };
      },
      providesTags: (result, error, { candidateCheckId }) => [
        { type: 'PortalCheck', id: candidateCheckId },
      ],
    }),

    /**
     * Get sub check result file.
     * Returns a signed HTTPS URL when backend provides one, or a data URL from the stream.
     */
    getSubCheckResultFile: builder.query({
      queryFn: async ({ subCheckId }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/checks/sub/${subCheckId}/result`,
          responseHandler: async response => {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            });
          },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: response.data };
      },
      providesTags: (result, error, { subCheckId }) => [{ type: 'PortalSubCheck', id: subCheckId }],
    }),

    /**
     * Get consent files
     */
    getConsentFiles: builder.query({
      query: () => '/consent/files',
      transformResponse: response => response.result,
      providesTags: ['PortalConsentFiles'],
    }),

    // ==========================================
    // UPLOAD ENDPOINTS
    // ==========================================

    /**
     * Sign upload (get presigned URL for S3)
     */
    signUpload: builder.query({
      query: params => `/uploads/sign?${toQueryParams(params)}`,
      transformResponse: response => response.result,
    }),

    /**
     * Get upload by ID
     */
    getUpload: builder.query({
      query: id => `/uploads/${id}`,
      transformResponse: response => response.result,
    }),

    /**
     * Create upload
     */
    createUpload: builder.mutation({
      query: params => ({
        url: '/uploads',
        method: 'POST',
        body: params,
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalUploads'],
    }),

    /**
     * Delete upload
     */
    deleteUpload: builder.mutation({
      query: id => ({
        url: `/uploads/${id}`,
        method: 'DELETE',
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalUploads'],
    }),

    /**
     * Update candidate check
     */
    updateCandidateCheck: builder.mutation({
      query: ({ candidateCheckId, params }) => ({
        url: `/checks/${candidateCheckId}`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalDashboard'],
    }),

    /**
     * Submit check dispute
     */
    submitCheckDispute: builder.mutation({
      query: ({ candidateCheckId, values }) => ({
        url: `/checks/${candidateCheckId}/dispute`,
        method: 'POST',
        body: { values },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalDashboard'],
    }),

    /**
     * Update a referee's own details (only while not yet completed)
     */
    updateReferee: builder.mutation({
      query: ({ refereeId, params }) => ({
        url: `/referees/${refereeId}`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalDashboard'],
    }),

    /**
     * Replace a referee with a new one (only while not yet completed)
     */
    replaceReferee: builder.mutation({
      query: ({ refereeId, params }) => ({
        url: `/referees/${refereeId}/replace`,
        method: 'POST',
        body: params,
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalDashboard'],
    }),

    /**
     * Get the dynamic candidate_form used to nominate/replace a referee
     */
    getRefereeForm: builder.query({
      query: refereeId => `/referees/${refereeId}/form`,
      transformResponse: response => response.result,
    }),

    getMedicalSites: builder.mutation({
      query: ({ candidateCheckId, postalCode, searchRadius, address }) => ({
        url: `/checks/${candidateCheckId}/drug_screen/sites`,
        method: 'POST',
        body: {
          postal_code: postalCode,
          ...(searchRadius && { search_radius: searchRadius }),
          ...(address && { address }),
        },
      }),
      transformResponse: response => response.result,
    }),

    scheduleMedical: builder.mutation({
      query: ({
        candidateCheckId,
        collectionSiteLinkId,
        siteName,
        siteAddress,
        siteCity,
        siteState,
        sitePhone,
      }) => ({
        url: `/checks/${candidateCheckId}/drug_screen/schedule`,
        method: 'POST',
        body: {
          collection_site_link_id: collectionSiteLinkId,
          site_name: siteName,
          site_address: siteAddress,
          site_city: siteCity,
          site_state: siteState,
          site_phone: sitePhone,
        },
      }),
      transformResponse: response => response.result,
    }),
  }),
});

export const {
  // Public endpoints
  useLazyDecodeInvitationQuery,
  useDecodeInvitationQuery,
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useLoginWithPasswordMutation,
  // Authenticated endpoints
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useLogoutPortalMutation,
  useSetPasswordMutation,
  useSelectContextMutation,
  // Protected endpoints
  useUpdateCandidateCheckMutation,
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useGetCheckResultFileQuery,
  useLazyGetCheckResultFileQuery,
  useGetSubCheckResultFileQuery,
  useLazyGetSubCheckResultFileQuery,
  useSubmitCheckDisputeMutation,
  useUpdateRefereeMutation,
  useReplaceRefereeMutation,
  useGetRefereeFormQuery,
  useGetMedicalSitesMutation,
  useScheduleMedicalMutation,
  // Upload endpoints
  useLazySignUploadQuery,
  useGetUploadQuery,
  useLazyGetUploadQuery,
  useCreateUploadMutation,
  useDeleteUploadMutation,
  // Consent endpoints
  useGetConsentFilesQuery,
  useLazyGetConsentFilesQuery,
} = candidatePortalApi;
